/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { ProgressBar } from 'sharedUi/ProgressBar';
import { Button } from 'sharedUi/Button';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { formatDate } from 'sharedUi/dateUtils';
import { EmptyState, Metric, PageSkeleton, Rise, SectionLabel, StatusChip } from '../../components/common/panel';
import ScreenHeader from '../../components/common/screen-header';
import AiFeedbackBar from '../../components/ai-feedback/ai-feedback-bar';
import { PANEL, PRETTY } from '../../components/common/ui-kit';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useHasPermission } from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../constants/permissions';
import { pipelinesService } from '../../services/pipelines.service';
import type { PipelineArtifact, PipelineRun } from '../../types';
import { artifactTone, runTone } from '../pipelines/pipeline-status';

const TERMINAL = ['COMPLETED', 'PARTIAL_FAILED', 'FAILED', 'CANCELLED'];

const eth = 'px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-ink-muted';

/** Counts read better grouped; an absent one is not a zero. */
const num = (n?: number | null) => (n == null ? '—' : n.toLocaleString());

/** Spec §12.3 — Pipeline Run Detail. Polls while QUEUED/RUNNING. */
const PipelineRunView = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({ entity: 'pipeline-run', basePath: '/pipeline-runs' });
  const { getViewUrl: pipelineUrl } = useSecureNavigation({ entity: 'pipeline', basePath: '/pipelines' });
  const queryClient = useQueryClient();
  const canRun = useHasPermission(PERMISSIONS.PIPELINE_RUN_EXECUTE);
  useBreadcrumbTitle('Pipeline Run');

  const { data, isLoading, isError, error } = useQuery<PipelineRun>({
    queryKey: ['pipeline-run', id],
    queryFn: () => pipelinesService.getRun(id!),
    enabled: isReady && !!id,
    refetchInterval: (q) => (TERMINAL.includes(String(q.state.data?.status)) ? false : 2000),
  });
  // A run records its artifact even when the build failed, so what it produced
  // is the artifact's own status — not the run's having a ref.
  const { data: artifact } = useQuery<PipelineArtifact>({
    queryKey: ['pipeline-artifact', data?.artifactRef, data?.status],
    queryFn: () => pipelinesService.getArtifact(data!.artifactRef!),
    enabled: !!data?.artifactRef,
  });
  const cancel = useMutation({
    mutationFn: () => pipelinesService.cancelRun(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pipeline-run', id] }),
  });

  if (!isReady || !id || isLoading) return <PageSkeleton panels={1} />;
  if (isError || !data) {
    return (
      <div className="p-4">
        <EmptyState
          icon="pi pi-exclamation-triangle"
          title="This run could not be loaded"
          hint={(error as any)?.message || 'It may have been deleted, or the link points at another project.'}
          action={<span className="ui-press inline-flex"><Button label="Back to runs" icon="pi pi-arrow-left" severity="secondary" outlined size="small" onClick={() => navigateToList()} /></span>}
        />
      </div>
    );
  }

  const inProgress = data.status === 'QUEUED' || data.status === 'RUNNING';
  const st = data.statistics || {};
  const prog = data.progress || {};
  const pct = prog.sourcesTotal ? Math.min(100, Math.round(((prog.sourcesDone ?? 0) / prog.sourcesTotal) * 100)) : 0;

  return (
    <div className="p-4" data-testid="SCR-PipelineRun-View">
      <ScreenHeader
        icon="pi pi-play-circle"
        onBack={() => navigateToList()}
        title="Pipeline Run"
        right={
          <>
            <span className="font-mono text-xs text-ink-muted">{data.pipelineRunId}</span>
            <StatusChip tone={runTone(data.status)}>{data.status}</StatusChip>
            {inProgress && <ProgressSpinner style={{ width: '14px', height: '14px' }} strokeWidth="6" />}
            {inProgress && canRun && (
              <span className="ui-press inline-flex">
                <Button label={cancel.isPending ? 'Cancelling…' : 'Cancel run'} icon={cancel.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-times'} severity="danger" outlined size="small"
                        disabled={cancel.isPending} onClick={() => cancel.mutate()} data-testid="PipelineRun.Cancel" />
              </span>
            )}
          </>
        }
      />

      {cancel.isError && <Message severity="error" className="w-full mb-2" text={(cancel.error as any)?.response?.data?.message || 'Could not cancel this run.'} />}

      <Rise i={1} className={`${PANEL} flex flex-col gap-4 p-4`}>
        <div className="text-sm text-gray-600 dark:text-gray-300">
          Pipeline <span className="font-mono text-gray-800 dark:text-gray-100">{data.pipelineCode}</span>
          {data.pipelineVersion != null && <span className="tabular-nums"> · recipe v{data.pipelineVersion}</span>}
        </div>

        {inProgress && (
          <div data-testid="PipelineRun.Progress">
            <div className="mb-1.5 flex items-center justify-between text-xs text-ink-muted">
              <span className="tabular-nums">{prog.stage ? `${prog.stage.toLowerCase()} · ` : ''}{prog.sourcesDone ?? 0} / {prog.sourcesTotal ?? 0} sources</span>
              <span className="tabular-nums font-semibold">{pct}%</span>
            </div>
            <ProgressBar value={pct} showValue={false} style={{ height: '6px' }} />
          </div>
        )}

        {data.error && <Message severity={data.status === 'CANCELLED' ? 'warn' : 'error'} className="w-full" text={data.error} />}

        {/* §12.3 fields */}
        <div>
          <SectionLabel icon="pi pi-chart-bar">What the run produced</SectionLabel>
          <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-4">
            <Metric label="Sources requested" value={num(st.sourcesRequested)} />
            <Metric label="Sources processed" value={num(st.sourcesProcessed)} />
            <Metric label="Failed sources" value={num(st.sourcesFailed)} tone={st.sourcesFailed ? 'danger' : undefined} />
            <Metric label="Chunks created" value={num(st.chunksCreated)} />
            <Metric label="Embeddings created" value={num(st.embeddingsCreated)} />
            <Metric label="Embedding tokens" value={num(st.embeddingTokens)} />
            <Metric label="Cost" value={st.embeddingCost != null ? `${st.embeddingCost} ${st.currency || ''}` : '—'} />
            <Metric label="Duration" value={st.durationSeconds ? `${st.durationSeconds.toFixed(1)}s` : '—'} />
          </div>
        </div>

        <div>
          <SectionLabel icon="pi pi-clock">Timing</SectionLabel>
          <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3">
            <Metric label="Started" value={formatDate(data.startedAt || data.createdAt)} />
            <Metric label="Completed" value={data.completedAt ? formatDate(data.completedAt) : '—'} />
            <div className="min-w-0">
              <div className="text-[11px] font-medium uppercase tracking-[0.05em] text-ink-muted">Produced artifact</div>
              <div className="mt-0.5 text-[15px] font-semibold" data-testid="PipelineRun.Artifact">
                {!data.artifactRef || artifact?.status === 'FAILED'
                  ? <span className="text-sm font-normal text-ink-muted">
                      {inProgress ? '—' : `None — the ${artifact?.status === 'FAILED' ? 'build failed' : 'run produced none'}`}
                    </span>
                  : (
                    <span className="flex flex-wrap items-center gap-1.5">
                      <a className="font-mono text-[13px] text-indigo-600 underline decoration-from-font underline-offset-2 hover:text-indigo-700 dark:text-indigo-400"
                        href={`${pipelineUrl(data.pipelineRef)}?tab=artifacts`}>
                        {artifact?.artifactCode
                          ? artifact.artifactCode.split('.').map((part, i) => <span key={i}>{i > 0 && <>.<wbr /></>}{part}</span>)
                          : `artifact ${data.artifactRef.slice(-6)}`}
                      </a>
                      {artifact && <StatusChip tone={artifactTone(artifact.status)} dot={false}>{artifact.status}</StatusChip>}
                    </span>
                  )}
              </div>
            </div>
          </div>
        </div>

        {data.configurationSnapshot && (
          <details>
            <summary className="ui-press cursor-pointer select-none text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-muted">
              Configuration snapshot
            </summary>
            <pre className="ui-inset mt-2 max-h-64 overflow-auto p-3 text-xs leading-relaxed">{JSON.stringify(data.configurationSnapshot, null, 2)}</pre>
          </details>
        )}

        {!!data.errors?.length && (
          <div>
            <div className="flex items-baseline justify-between gap-2">
              <SectionLabel icon="pi pi-exclamation-circle">Errors</SectionLabel>
              <span className="text-xs tabular-nums text-ink-muted">{data.errors.length}</span>
            </div>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-line">
                    <th className={eth}>Source</th><th className={eth}>Stage</th><th className={eth}>Code</th><th className={eth}>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {data.errors.map((e, i) => (
                    <tr key={i} className="ui-row border-b border-gray-50 align-top last:border-0 dark:border-gray-700">
                      <td className="px-3 py-2 font-mono">{e.sourceName}</td>
                      <td className="px-3 py-2">{e.stage}</td>
                      <td className="px-3 py-2 font-mono">{e.code}</td>
                      <td className="ui-ink ui-ink--bad px-3 py-2" style={PRETTY}>{e.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* On the run, once it has finished: what a person can judge is what the
            run produced, and pipelineRunId is what lets a reader reproduce the
            judgement later. A run still in flight has produced nothing to judge. */}
        {TERMINAL.includes(String(data.status)) && (
          <AiFeedbackBar
            className="border-t border-line pt-3"
            componentType="WORKFLOW"
            componentCode={data.pipelineCode ? `pipeline:${data.pipelineCode}` : undefined}
            componentId={data.pipelineRunId || id}
            componentVersion={data.pipelineVersion != null
              ? `${data.pipelineCode}:v${data.pipelineVersion}` : undefined}
            runtimeContext={{
              workflowInstanceId: data.pipelineRunId || id,
              currentStep: prog.stage || data.status,
              elapsedTime: st.durationSeconds ?? null,
              status: data.status,
              pipelineCode: data.pipelineCode || null,
              pipelineVersion: data.pipelineVersion ?? null,
              sourcesProcessed: st.sourcesProcessed ?? null,
              sourcesFailed: st.sourcesFailed ?? null,
              chunksCreated: st.chunksCreated ?? null,
              embeddingsCreated: st.embeddingsCreated ?? null,
              durationSeconds: st.durationSeconds ?? null,
              artifactCode: artifact?.artifactCode || null,
            }}
            meta={artifact?.artifactCode || undefined}
            testId="PipelineRun.Feedback"
          />
        )}
      </Rise>
    </div>
  );
};

export default PipelineRunView;
